import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { HERO_IMG } from "@/data/content";
import { Overline } from "@/components/shared";

export const Hero = () => {
  const { t } = useLang();
  const h = t.hero;

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" data-testid="hero" className="relative flex min-h-screen items-end overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/55 to-white/95" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/70 to-transparent" />
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-6 pb-20 pt-40 sm:px-12 md:pb-28">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <Overline>{h.overline}</Overline>
        </motion.div>

        <motion.h1
          data-testid="hero-title"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-4xl font-heading text-5xl font-light leading-[1.05] tracking-tighter text-zinc-900 sm:text-6xl md:text-7xl"
        >
          {h.title}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-base leading-relaxed text-zinc-600 sm:text-lg"
        >
          {h.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => go("contact")}
            className="group inline-flex items-center justify-center gap-2 bg-zinc-900 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-all hover:bg-zinc-700"
          >
            {h.ctaPrimary}
            <ArrowDownRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => go("about")}
            className="inline-flex items-center justify-center border border-zinc-900 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-zinc-900 transition-colors hover:bg-zinc-900 hover:text-white"
          >
            {h.ctaSecondary}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="mt-16 grid max-w-2xl grid-cols-3 gap-6 border-t border-zinc-300/70 pt-8"
        >
          {h.stats.map((s) => (
            <div key={s.label} data-testid="hero-stat">
              <div className="font-heading text-3xl font-light text-zinc-900 sm:text-4xl">{s.value}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
